/**
 * sl-apue + sl-fwcn: SeatManagerDialog — Unified stepper dialog for seat addon
 * sl-7vu4: Uses graduated tiered pricing instead of flat per-seat price
 *
 * Single flow for both first purchase and quantity updates:
 * 1. User picks quantity in stepper (with baseline + total context)
 * 2. Clicks CTA → PurchaseConfirmDialog (§312j/§356 BGB)
 * 3. After consent: purchaseSeats (first) or updateSeatCount (update)
 *    sl-fwcn: No Stripe Checkout redirect — seats are added as items on plan subscription.
 */
import { ConfirmModal, notify } from '@affine/component';
import {
  buildLabeledPurchaseOrderSummary,
  PurchaseConfirmDialog,
} from '@affine/core/components/consent';
import {
  SeatAddonService,
  WorkspaceSubscriptionService,
} from '@affine/core/modules/cloud';
import { formatPrice } from '@affine/core/utils/format-price';
import {
  calculateGraduatedTotal,
  parseSeatTiers,
} from '@affine/core/utils/graduated-pricing';
import { SubscriptionRecurring } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import * as styles from './styles.css';

const MIN_SEATS = 1;

interface SeatManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentQuantity: number;
}

export const SeatManagerDialog = ({
  open,
  onOpenChange,
  currentQuantity,
}: SeatManagerDialogProps) => {
  const t = useI18n();
  const lang = t.language?.startsWith('en') ? 'en' : 'de';
  const locale = lang === 'en' ? 'en-US' : 'de-DE';
  const seatAddonService = useService(SeatAddonService);
  const wsSubService = useService(WorkspaceSubscriptionService);
  const memberQuota = useLiveData(seatAddonService.seatAddon.memberQuota$);
  const wsSub = useLiveData(wsSubService.subscription.subscription$);
  const isYearly = wsSub?.recurring === SubscriptionRecurring.Yearly;

  const isFirstPurchase = currentQuantity === 0;

  const [quantity, setQuantity] = useState(
    isFirstPurchase ? MIN_SEATS : currentQuantity
  );
  const [showConfirm, setShowConfirm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const seatTiers = useMemo(
    () => parseSeatTiers(memberQuota?.seatTiers),
    [memberQuota?.seatTiers]
  );

  const activeTiers = isYearly ? seatTiers?.yearly : seatTiers?.monthly;

  const totalPrice = useMemo(
    () => (activeTiers ? calculateGraduatedTotal(activeTiers, quantity) : 0),
    [activeTiers, quantity]
  );

  const priceLabel = useMemo(() => {
    const formattedPrice = formatPrice(totalPrice, 'eur', locale);
    const interval = isYearly
      ? t['com.streamlain.payment.pricing.per-year']()
      : t['com.streamlain.payment.pricing.per-month']();
    return `${formattedPrice} ${interval}`;
  }, [totalPrice, isYearly, locale, t]);

  const productName = useMemo(() => {
    return t['com.streamlain.payment.seat-addon.manager.product-name']({
      count: String(quantity),
    });
  }, [quantity, t]);

  // Seat quota context
  const baseLimit = memberQuota?.baseLimit ?? 0;
  const currentUsage = memberQuota?.currentUsage ?? 0;
  const maxExtraSeats = memberQuota?.maxExtraSeats ?? 0;
  const maximumQuantity =
    maxExtraSeats === -1 ? Number.MAX_SAFE_INTEGER : maxExtraSeats;
  const totalSeats = baseLimit + quantity;

  const hasChanged = quantity !== currentQuantity;
  const exceedsPlanLimit = maximumQuantity === 0 || quantity > maximumQuantity;

  const handleDecrement = useCallback(() => {
    setQuantity(q => Math.max(MIN_SEATS, q - 1));
  }, []);

  const handleIncrement = useCallback(() => {
    setQuantity(q => Math.min(maximumQuantity, q + 1));
  }, [maximumQuantity]);

  // Step 1: CTA in stepper dialog → open PurchaseConfirmDialog
  const handleCTAClick = useCallback(() => {
    if (exceedsPlanLimit) return;
    setShowConfirm(true);
  }, [exceedsPlanLimit]);

  // Step 2: User confirms in PurchaseConfirmDialog → execute action
  const handleConfirmPurchase = useCallback(async () => {
    if (exceedsPlanLimit) return;
    setIsProcessing(true);
    try {
      if (isFirstPurchase) {
        // sl-fwcn: Direct API call — seats are added as items on plan subscription.
        // No Stripe Checkout redirect needed (payment method already on file).
        await seatAddonService.seatAddon.purchaseSeats(quantity);
        notify.success({
          title: t['com.streamlain.payment.seat-addon.update.success'](),
        });
      } else {
        // Update existing quantity
        await seatAddonService.seatAddon.updateSeatCount(quantity, true);
        notify.success({
          title: t['com.streamlain.payment.seat-addon.update.success'](),
        });
      }
      setShowConfirm(false);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      notify.error({
        title: t['com.streamlain.payment.seat-addon.error'](),
        message,
      });
      console.error('Failed to process seat purchase:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [
    exceedsPlanLimit,
    isFirstPurchase,
    quantity,
    seatAddonService,
    onOpenChange,
    t,
  ]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setQuantity(isFirstPurchase ? MIN_SEATS : currentQuantity);
        setShowConfirm(false);
      }
      onOpenChange(newOpen);
    },
    [isFirstPurchase, currentQuantity, onOpenChange]
  );

  const intervalKey = isYearly
    ? 'com.streamlain.payment.pricing.per-year'
    : 'com.streamlain.payment.pricing.per-month';

  return (
    <>
      <ConfirmModal
        open={open && !showConfirm}
        onOpenChange={handleOpenChange}
        title={t['com.streamlain.payment.seat-addon.manager.title']()}
        confirmText={t['com.streamlain.payment.seat-addon.manager.confirm']()}
        onConfirm={handleCTAClick}
        confirmButtonOptions={{
          disabled: exceedsPlanLimit || (!isFirstPurchase && !hasChanged),
        }}
      >
        <div className={styles.seatManagerContent}>
          {/* Seat quota context: baseline + extra + total */}
          <div className={styles.seatQuotaSummary}>
            <div className={styles.seatQuotaRow}>
              <span>
                {t['com.streamlain.payment.seat-addon.manager.plan-seats']()}
              </span>
              <span>{baseLimit}</span>
            </div>
            <div className={styles.seatQuotaRow}>
              <span>
                {t['com.streamlain.payment.seat-addon.manager.extra-seats']()}
              </span>
              <span>{quantity}</span>
            </div>
            <div className={styles.seatQuotaRowTotal}>
              <span>
                {t['com.streamlain.payment.seat-addon.manager.total-seats']()}
              </span>
              <span>{totalSeats}</span>
            </div>
          </div>

          {currentUsage > 0 && (
            <div className={styles.seatQuotaUsage}>
              {t['com.streamlain.payment.seat-addon.manager.currently-used']({
                used: String(currentUsage),
                total: String(totalSeats),
              })}
            </div>
          )}

          {/* Quantity stepper */}
          <div className={styles.seatStepper}>
            <button
              className={styles.seatStepperButton}
              onClick={handleDecrement}
              disabled={quantity <= MIN_SEATS}
            >
              -
            </button>
            <span
              className={styles.seatStepperValue}
              data-testid="seat-stepper-value"
            >
              {quantity}
            </span>
            <button
              className={styles.seatStepperButton}
              onClick={handleIncrement}
              disabled={quantity >= maximumQuantity}
            >
              +
            </button>
          </div>

          {/* Price breakdown */}
          <div className={styles.seatPriceBreakdown}>
            {t['com.streamlain.payment.seat-addon.manager.price-breakdown']({
              count: String(quantity),
              unitPrice: formatPrice(
                quantity > 0 ? Math.round(totalPrice / quantity) : 0,
                'eur'
              ),
              totalPrice: formatPrice(totalPrice, 'eur'),
            })}{' '}
            {t[intervalKey]()}
          </div>

          <div className={styles.seatInfoText}>
            {t['com.streamlain.payment.seat-addon.manager.info']()}
          </div>
        </div>
      </ConfirmModal>

      {/* §312j/§356 BGB: Per-transaction consent via PurchaseConfirmDialog */}
      <PurchaseConfirmDialog
        open={showConfirm}
        onOpenChange={confirmOpen => {
          if (!confirmOpen) setShowConfirm(false);
        }}
        productName={productName}
        priceLabel={priceLabel}
        orderSummary={buildLabeledPurchaseOrderSummary({
          isSubscription: true,
          language: lang,
          productName,
          totalPriceLabel: priceLabel,
        })}
        isSubscription={true}
        isLoading={isProcessing}
        onConfirm={() => void handleConfirmPurchase()}
      />
    </>
  );
};
